import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, Share2, Gift, DollarSign, Users, Check, Loader2, QrCode, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Cleaner-side referral panel. Pulls the cleaner's referral code,
 * shareable URL, lifetime usage, pending and paid credits.
 */
const ReferralPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null); // "code" | "url" | null
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  const downloadQR = () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `matchmycleaner-referral-${data?.code || 'qr'}.png`;
    a.click();
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/promo/my-referral`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load referral info');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Older browsers / non-https — fall back to selecting + execCommand
      const tmp = document.createElement('textarea');
      tmp.value = text;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(tmp);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  const nativeShare = async () => {
    if (!data) return;
    const text = `Get 10% off your first cleaning with Match My Cleaner. Use my code: ${data.code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Match My Cleaner', text, url: data.share_url });
      } catch { /* user cancelled */ }
    } else {
      copy(`${text}\n${data.share_url}`, 'url');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-secondary text-sm py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your referral info…
      </div>
    );
  }

  if (error || !data) {
    return <div className="text-sm text-red-700 py-2" data-testid="referral-error">{error}</div>;
  }

  const stats = [
    { icon: Users, label: 'Referred', value: data.uses_count, color: 'text-blue-600 bg-blue-50' },
    { icon: DollarSign, label: 'Pending credit', value: `$${(data.pending_credit || 0).toFixed(2)}`, color: 'text-amber-700 bg-amber-50' },
    { icon: Check, label: 'Paid out', value: `$${(data.paid_credit || 0).toFixed(2)}`, color: 'text-emerald-700 bg-emerald-50' },
  ];

  return (
    <div className="bg-surface border border-border rounded-2xl p-5 space-y-4" data-testid="referral-panel">
      <div className="flex items-start gap-3">
        <div className="bg-primary-light text-primary p-2 rounded-xl shrink-0">
          <Gift className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-text-primary leading-tight">
            Refer a friend, earn $$$
          </h3>
          <p className="text-sm text-text-secondary leading-snug mt-0.5">
            Share your code with friends or neighbors. When they book, they get <strong>{data.discount_value}% off</strong> their first cleaning — and you earn a credit on their booking.
          </p>
        </div>
      </div>

      {/* The code */}
      <div className="bg-surface-muted rounded-xl p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Your code</p>
          <p className="font-mono text-xl font-bold text-text-primary truncate" data-testid="referral-code">{data.code}</p>
        </div>
        <button
          onClick={() => copy(data.code, 'code')}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 bg-surface border border-border rounded-full hover:border-primary hover:text-primary transition-colors"
          data-testid="copy-referral-code"
        >
          {copied === 'code' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied === 'code' ? 'Copied!' : 'Copy code'}
        </button>
      </div>

      {/* Shareable URL */}
      <div className="bg-surface-muted rounded-xl p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Share link</p>
          <p className="text-sm text-text-primary font-mono truncate" data-testid="referral-url">{data.share_url}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => copy(data.share_url, 'url')}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 bg-surface border border-border rounded-full hover:border-primary hover:text-primary transition-colors"
            data-testid="copy-referral-url"
          >
            {copied === 'url' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied === 'url' ? 'Copied!' : 'Copy link'}
          </button>
          <button
            onClick={nativeShare}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors"
            data-testid="native-share-referral"
          >
            <Share2 className="w-3 h-3" />
            Share
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {stats.map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-surface-muted rounded-xl p-3 text-center">
            <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${color} mb-1`}>
              <Icon className="w-4 h-4" />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary leading-tight">{label}</p>
            <p className="text-base font-bold text-text-primary leading-tight" data-testid={`referral-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* QR code — toggleable, downloadable for flyers/business cards */}
      <div className="border-t border-border pt-4" data-testid="referral-qr-section">
        <button
          onClick={() => setShowQR(v => !v)}
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 bg-primary-light/30 text-primary rounded-full hover:bg-primary-light/50 transition-colors"
          data-testid="toggle-referral-qr"
        >
          <QrCode className="w-4 h-4" />
          {showQR ? 'Hide QR code' : 'Show QR code for flyers & cards'}
        </button>
        {showQR && (
          <div className="mt-4 flex flex-col items-center gap-3 bg-white rounded-2xl p-5 border-2 border-dashed border-primary/30">
            <div ref={qrRef} data-testid="referral-qr-code">
              <QRCodeCanvas
                value={data.share_url}
                size={220}
                fgColor="#1d4f3a"
                bgColor="#ffffff"
                level="H"
                includeMargin
              />
            </div>
            <p className="text-xs text-text-secondary text-center max-w-xs">
              Scan to sign up with your code <strong className="font-mono text-primary">{data.code}</strong>.
              Perfect for flyers, business cards, or door hangers.
            </p>
            <button
              onClick={downloadQR}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors"
              data-testid="download-referral-qr"
            >
              <Download className="w-4 h-4" /> Download PNG
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralPanel;
